import { useSeo, breadcrumbSchema, SITE_NAME, SITE_URL } from "@/lib/seo";
import { Button } from "@/components/ui/button";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { trackCalendarOpen, trackContactIntent, trackEvent } from "@/lib/analytics";
import { CheckCircle2 } from "lucide-react";
import { Link } from "wouter";
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetHeader, SheetDescription } from "@/components/ui/sheet";
import { useRef, useState } from "react";

const formSchema = z.object({
  name: z.string().trim().min(2, "Name is required").max(120),
  company: z.string().trim().min(2, "Company is required").max(120),
  role: z.string().trim().min(2, "Role is required").max(120),
  email: z.string().trim().email("Invalid email address").max(254),
  message: z.string().trim().min(10, "Please provide more details about your needs").max(5000),
  website: z.string().max(200),
});

export default function Contact() {
  useSeo({
    title: "Contact Relevant Dealer | Websites, Marketing & Team Development",
    description:
      "Talk with Relevant Dealer about custom websites, analytics, SEO and AI visibility, consent tools, marketing, recruiting, and training for your dealership.",
    keywords: [
      "contact Relevant Dealer",
      "automotive marketing strategy call",
      "fractional CMO consultation",
      "dealership marketing audit",
      "automotive marketing agency contact",
    ],
    canonicalPath: "/contact",
    jsonLd: [
      breadcrumbSchema([
        { name: "Home", path: "/" },
        { name: "Contact", path: "/contact" },
      ]),
      {
        "@context": "https://schema.org",
        "@type": "ContactPage",
        name: "Contact Relevant Dealer",
        url: `${SITE_URL}/contact`,
        about: {
          "@type": "Organization",
          name: SITE_NAME,
          url: SITE_URL,
          email: "inquiries@relevantdealer.com",
          telephone: "+1-888-396-4252",
        },
      },
    ],
  });

  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const submitting = useRef(false);
  const submissionId = useRef<string | null>(null);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      company: "",
      role: "",
      email: "",
      message: "",
      website: "",
    },
  });

  async function onSubmit(values: z.infer<typeof formSchema>) {
    if (submitting.current) return;
    submitting.current = true;
    setSubmitError("");
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 20000);
    try {
      // Keep only the retry identifier across reloads, never the inquiry content.
      if (!submissionId.current) {
        try {
          submissionId.current = sessionStorage.getItem("contact-submission-id");
        } catch { /* Storage may be blocked by the browser. */ }
        submissionId.current ||= crypto.randomUUID();
        try {
          sessionStorage.setItem("contact-submission-id", submissionId.current);
        } catch { /* In-memory retry protection still applies. */ }
      }
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...values, submissionId: submissionId.current }),
        signal: controller.signal,
      });
      const result = await response.json().catch(() => null);
      if (!response.ok || result?.accepted !== true || typeof result?.id !== "string" || !result.id) {
        throw new Error(response.status === 429
          ? "Too many attempts. Please wait a few minutes before trying again, or use the email or phone link."
          : "We couldn't confirm your request was received. Your details are still here—please try again, or use the email or phone link.");
      }
      setIsSubmitted(true);
      // Confirmed API acceptance only, never clicks, failed validation, or errors.
      // No form values or provider identifiers are sent to analytics.
      trackEvent("generate_lead", { method: "contact_form", page_path: "/contact" });
      form.reset();
      submissionId.current = null;
      try { sessionStorage.removeItem("contact-submission-id"); } catch { /* Optional storage. */ }
    } catch (error) {
      setSubmitError(error instanceof Error && error.name !== "AbortError" && error.message.startsWith("Too many attempts.")
        ? error.message
        : "We couldn't confirm your request was received. Your details are still here—please try again, or use the email or phone link.");
    } finally {
      window.clearTimeout(timeout);
      submitting.current = false;
    }
  }

  return (
    <div className="pt-24 min-h-screen bg-background">
      <div className="container mx-auto px-4 max-w-7xl py-12 lg:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          
          <div>
            <h1 className="text-5xl font-display font-bold mb-3 text-foreground">Contact Relevant Dealer</h1>
            <p className="text-2xl font-display font-semibold text-foreground mb-6">Here to serve. Ready to listen.</p>
            <p className="text-xl text-muted-foreground mb-12">
              A better website. A clearer picture of your marketing. A team with the training to turn more conversations into appointments. Whatever your next priority, let's talk about what your dealership needs—not just what we sell.
            </p>
            
            <div className="bg-primary p-8 rounded-2xl text-white mb-8">
              <h2 className="text-2xl font-bold font-display mb-4">Schedule a Strategy Call</h2>
              <p className="text-white mb-6">Skip the form and book 30 minutes to discuss your goals, your current tools, and where you could use support.</p>
              <Sheet onOpenChange={(open) => { if (open) trackCalendarOpen(); }}>
                <SheetTrigger asChild>
                  <Button variant="secondary" className="bg-white text-primary hover:bg-secondary font-bold w-full h-12 text-lg">
                    View Calendar
                  </Button>
                </SheetTrigger>
                <SheetContent side="bottom" className="h-[92vh] p-0 flex flex-col gap-0 rounded-t-2xl">
                  <SheetHeader className="px-6 pt-6 pb-3 text-left border-b">
                    <SheetTitle className="font-display">Schedule a Strategy Call</SheetTitle>
                    <SheetDescription>
                      Pick a 30-minute slot that works for you. Tell us what you want to improve and we'll discuss practical next steps.
                    </SheetDescription>
                  </SheetHeader>
                  <iframe
                    src="https://calendly.com/stevelaureys/get-relevant-now?embed_domain=relevantdealer.com&embed_type=Inline&hide_gdpr_banner=1"
                    title="Schedule a Strategy Call with Relevant Dealer"
                    className="w-full flex-1 border-0"
                    loading="lazy"
                  />
                </SheetContent>
              </Sheet>
            </div>
            
            <div className="space-y-4 text-foreground">
              <p><strong>Email:</strong> <a href="mailto:inquiries@relevantdealer.com" onClick={() => trackContactIntent("email")} className="text-primary hover:underline">inquiries@relevantdealer.com</a></p>
              <p><strong>Phone:</strong> <a href="tel:+18883964252" onClick={() => trackContactIntent("phone")} className="text-primary hover:underline">888-396-4252</a></p>
            </div>
          </div>

          <div className="bg-white p-8 md:p-10 rounded-2xl border border-border shadow-sm">
            {!isSubmitted && (
              <div className="mb-8">
                <h2 className="text-2xl font-display font-bold mb-3">Tell us about your dealership</h2>
                <p className="text-muted-foreground">Share your biggest priority, the tools you use today, and any timeline you have in mind. Not sure which service fits? Start with the challenge—we can help you sort through the options.</p>
              </div>
            )}
            {isSubmitted ? (
              <div role="status" className="h-full flex flex-col items-center justify-center text-center py-12">
                <CheckCircle2 className="w-16 h-16 text-primary mb-6" />
                <h2 className="text-3xl font-display font-bold mb-4">Request Received</h2>
                <p className="text-muted-foreground text-lg mb-8">
                  Thank you for reaching out. Your request has been accepted for delivery to our team at support@relevantdealer.com.
                </p>
                <Button onClick={() => setIsSubmitted(false)} variant="outline">
                  Send another message
                </Button>
              </div>
            ) : (
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6" aria-busy={form.formState.isSubmitting}>
                  <fieldset disabled={form.formState.isSubmitting} className="space-y-6 min-w-0">
                  <div className="hidden" aria-hidden="true">
                    <label htmlFor="contact-website">Leave this field empty</label>
                    <input id="contact-website" tabIndex={-1} autoComplete="off" {...form.register("website")} />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <FormField
                      control={form.control}
                      name="name"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="font-semibold text-foreground">Name</FormLabel>
                          <FormControl>
                            <Input placeholder="John Doe" {...field} className="bg-secondary/50" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="font-semibold text-foreground">Email</FormLabel>
                          <FormControl>
                            <Input type="email" placeholder="john@dealership.com" {...field} className="bg-secondary/50" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <FormField
                      control={form.control}
                      name="company"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="font-semibold text-foreground">Company</FormLabel>
                          <FormControl>
                            <Input placeholder="Auto Group" {...field} className="bg-secondary/50" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="role"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="font-semibold text-foreground">Role</FormLabel>
                          <FormControl>
                            <Input placeholder="General Manager" {...field} className="bg-secondary/50" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <FormField
                    control={form.control}
                    name="message"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="font-semibold text-foreground">How can we help?</FormLabel>
                        <FormControl>
                          <Textarea 
                            placeholder="What would you like to improve? Share your goals, current tools, and any timing considerations..."
                            className="min-h-[150px] bg-secondary/50" 
                            {...field} 
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {submitError && <p role="alert" className="text-destructive text-sm">{submitError}</p>}
                  <Button type="submit" disabled={form.formState.isSubmitting} className="w-full bg-primary hover:bg-accent text-white h-14 text-lg font-bold">
                    {form.formState.isSubmitting ? "Sending request…" : submitError ? "Try Again" : "Submit Request"}
                  </Button>
                  </fieldset>
                  <p className="text-sm text-muted-foreground">Please don't include passwords or sensitive customer information. Learn how we handle your information in our <Link href="/privacy" className="text-primary underline underline-offset-4">Privacy Policy</Link>.</p>
                </form>
              </Form>
            )}
          </div>

        </div>

        <section className="mt-20 border-t border-border pt-12" aria-labelledby="contact-help">
          <p className="text-sm font-semibold uppercase tracking-widest text-primary mb-3">Start with what matters</p>
          <h2 id="contact-help" className="text-3xl md:text-4xl font-display font-bold mb-5">One priority or a bigger plan.</h2>
          <p className="text-lg text-muted-foreground max-w-3xl mb-10">With more than 30 years of automotive experience behind our approach, we understand that dealership needs don't fit neatly into a package. We can support one part of your business or help coordinate several—alongside the people and partners you already trust.</p>
          <div className="grid md:grid-cols-3 gap-10">
            <div>
              <h3 className="text-xl font-bold mb-3">Your website & technology</h3>
              <p className="text-muted-foreground mb-4">Discuss a custom dealership website, clearer reporting with Relevant Intelligence, or a branded Website User Consent Tool. Tell us what works today and what gets in the way.</p>
              <Link href="/dealership-websites" className="text-primary font-semibold underline underline-offset-4">Explore custom websites</Link>
            </div>
            <div>
              <h3 className="text-xl font-bold mb-3">Your visibility & marketing</h3>
              <p className="text-muted-foreground mb-4">Get help with SEO and AI visibility, paid search, social, video, email, and direct mail. Start with the audience you want to reach and the results you need to understand.</p>
              <Link href="/services/seo-geo-aeo" className="text-primary font-semibold underline underline-offset-4">Explore SEO & AI visibility</Link>
            </div>
            <div>
              <h3 className="text-xl font-bold mb-3">Your people & leadership</h3>
              <p className="text-muted-foreground mb-4">Talk about Fractional CMO support, recruiting, or in-store and online training for Sales, BDC, and Sales Leadership. Build structure around your team's next stage of growth.</p>
              <Link href="/services/training" className="text-primary font-semibold underline underline-offset-4">Explore team training</Link>
            </div>
          </div>
        </section>

        <section className="mt-16 rounded-2xl bg-secondary/50 p-8 md:p-10" aria-labelledby="contact-next">
          <h2 id="contact-next" className="text-2xl font-display font-bold mb-6">What we'll talk through</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div><h3 className="font-bold mb-2">Where you are today</h3><p className="text-muted-foreground">Your dealership, your team, your existing providers, and the challenge that brought you here.</p></div>
            <div><h3 className="font-bold mb-2">What needs to change</h3><p className="text-muted-foreground">The priorities, budget considerations, and timing that will shape a useful recommendation.</p></div>
            <div><h3 className="font-bold mb-2">The right next step</h3><p className="text-muted-foreground">Which services fit, what information we need, and whether a focused project or ongoing support makes sense.</p></div>
          </div>
          <p className="mt-8 text-muted-foreground">Still exploring? <Link href="/services" className="text-primary font-semibold underline underline-offset-4">View all services</Link> or <a href={`${import.meta.env.BASE_URL}brochure/`} target="_blank" rel="noopener" className="text-primary font-semibold underline underline-offset-4">browse our e-brochure (opens in a new window)</a> before we talk.</p>
        </section>
      </div>
    </div>
  );
}
